import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from './index';
import {
    fetchPhotos,
    updatePhoto,
    uploadPhoto,
    deletePhoto,
    setReplaceDialog,
    setDeleteConfirm,
    setDraggedPhoto,
    swapPhotos,
    Photo
} from '../slices/beforeAfterSlice';
import { useNotification } from '../../components/shared/NotificationSystem';
import { useLanguage as useLanguageContext } from '../../i18n/LanguageContext';
import { getCookie } from '../../utils/cookieUtils';

export const useBeforeAfter = () => {
    const dispatch = useAppDispatch();
    const { i18n } = useTranslation();
    const { language: contextLanguage } = useLanguageContext();
    const { showSuccess, showError } = useNotification();
    const {
        photos,
        isLoading,
        openReplaceDialog,
        replacePosition,
        showDeleteConfirm,
        photoToDelete,
        draggedPhoto,
        error
    } = useAppSelector(state => state.beforeAfter);

    const language = contextLanguage || getCookie('language') || getCookie('i18next') || 'ru';

    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error, showError]);

    const getPhotos = useCallback(() => {
        if (i18n.language !== language) {
            i18n.changeLanguage(language);
        }
        dispatch(fetchPhotos());
    }, [dispatch, i18n, language]);

    const handleReplace = useCallback((position: number) => {
        dispatch(setReplaceDialog({ open: true, position }));
    }, [dispatch]);

    const closeReplaceDialog = useCallback(() => {
        dispatch(setReplaceDialog({ open: false }));
    }, [dispatch]);

    const handleReplaceFile = useCallback(async (file: File) => {
        try {
            const existingPhoto = photos.find(p => p.position === replacePosition);

            if (existingPhoto) {
                const formData = new FormData();
                formData.append('photo_file', file);
                formData.append('language', language.toUpperCase());

                await dispatch(updatePhoto({
                    photoId: existingPhoto.id,
                    formData
                })).unwrap();
                showSuccess('beforeAfter.replaceSuccess');
            } else {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('language', language.toUpperCase());
                formData.append('position', String(replacePosition));

                await dispatch(uploadPhoto(formData)).unwrap();
                showSuccess('beforeAfter.uploadSuccess');
            }

            dispatch(setReplaceDialog({ open: false }));
            await dispatch(fetchPhotos());
        } catch (error) {
            showError('beforeAfter.errors.replaceFailed');
        }
    }, [dispatch, photos, replacePosition, showSuccess, showError, language]);

    const handleDelete = useCallback((id: number) => {
        dispatch(setDeleteConfirm({ show: true, photoId: id }));
    }, [dispatch]);

    const confirmDelete = useCallback(async () => {
        if (photoToDelete) {
            try {
                await dispatch(deletePhoto(photoToDelete)).unwrap();
                showSuccess('beforeAfter.deleteSuccess');
                await dispatch(fetchPhotos());
            } catch (error) {
                showError('beforeAfter.errors.deleteFailed');
            }
        }
        dispatch(setDeleteConfirm({ show: false, photoId: null }));
    }, [dispatch, photoToDelete, showSuccess, showError]);

    const cancelDelete = useCallback(() => {
        dispatch(setDeleteConfirm({ show: false, photoId: null }));
    }, [dispatch]);

    const handleDragStart = useCallback((photo: Photo) => {
        dispatch(setDraggedPhoto(photo));
    }, [dispatch]);

    const handleDrop = useCallback((targetPosition: number) => {
        dispatch(swapPhotos({ targetPosition }));
    }, [dispatch]);

    return {
        photos,
        isLoading,
        openReplaceDialog,
        replacePosition,
        showDeleteConfirm,
        photoToDelete,
        draggedPhoto,
        error,
        getPhotos,
        handleReplace,
        closeReplaceDialog,
        handleReplaceFile,
        handleDelete,
        confirmDelete,
        cancelDelete,
        handleDragStart,
        handleDrop
    };
}; 